import { useMemo } from "react"

import type { TowerPlotReqConfig } from "types/dashboard.types"
import type { EntityTelemetry } from "types/telemetries.types"
import type { TowerType } from "types/towers.types"

import useDateRange from "helpers/hooks/useDateRange"
import { useMultiTowerTelemetry } from "helpers/hooks/plots/useMultiTowerTelemetry"
import {
  TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD,
  TOWER_GLOBAL_LEAN_HEADING_SMOOTHED_FIELD,
} from "helpers/config/telemetryFieldDefs"

import TowerPositionIllustration from "./TowerPositionIllustration"

const positionReqConfig: TowerPlotReqConfig = {
  fields: [
    TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD,
    TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD,
    TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD,
    TOWER_GLOBAL_LEAN_HEADING_SMOOTHED_FIELD,
  ],
  method: ["last"],
  group: ["tower_id", "_field"],
  measurement: "tower_health",
}

interface LatestLean {
  front: number
  right: number
  magnitude: number
  heading: number
  time: number
}

const getLatestLean = (data: EntityTelemetry[] | undefined): LatestLean => {
  const latest: Record<string, { value: number; time: number }> = {}
  for (const row of data ?? []) {
    if (!row._field) continue
    const time = new Date(row._time).getTime()
    const prev = latest[row._field]
    if (!prev || time > prev.time)
      latest[row._field] = { value: Number(row._value), time }
  }
  const times = Object.values(latest).map((entry) => entry.time)
  return {
    front: latest[TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD]?.value ?? 0,
    right: latest[TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD]?.value ?? 0,
    magnitude: latest[TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD]?.value ?? 0,
    heading: latest[TOWER_GLOBAL_LEAN_HEADING_SMOOTHED_FIELD]?.value ?? 0,
    time: times.length ? Math.max(...times) : Number.NEGATIVE_INFINITY,
  }
}

interface TowerPositionProps {
  towerIds: number[]
  towerType?: TowerType
}

const TowerPosition = ({ towerIds, towerType }: TowerPositionProps) => {
  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools("tower"),
    [getDateRangeTools],
  )

  const { data, isFetching, error } = useMultiTowerTelemetry({
    towerIds,
    reqConfig: positionReqConfig,
    dateRange,
    isDateRangeValid,
  })

  const latest = useMemo(() => getLatestLean(data), [data])

  if (!Number.isFinite(latest.time)) return null

  return (
    <TowerPositionIllustration
      frontTilt={latest.front}
      rightTilt={latest.right}
      magnitude={latest.magnitude}
      heading={latest.heading}
      time={latest.time}
      towerType={towerType}
      isLoading={isFetching}
      isError={Boolean(error)}
    />
  )
}

export default TowerPosition
